import React from 'react';
import { FaCheck, FaTimes, FaShieldAlt } from 'react-icons/fa';

interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumbers: boolean;
  hasSpecialChars: boolean;
  isNotCommon: boolean;
}

interface PasswordStrengthMeterProps {
  password: string;
  requirements?: {
    minLength?: number;
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireNumbers?: boolean;
    requireSpecialChars?: boolean;
  };
  showRequirements?: boolean;
}

export const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  requirements = {
    minLength: 12,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true
  },
  showRequirements = true
}) => {
  // Common weak passwords
  const commonPasswords = [
    'password', '123456', '123456789', '12345678', '12345', '1234567', 'qwerty',
    'abc123', 'password123', 'admin', 'letmein', 'welcome', 'monkey', 'dragon',
    'master', 'shadow', 'football', 'baseball', 'superman', 'batman', 'trustno1',
    'hello', 'login', 'passw0rd', 'p@ssword', 'password1', 'password!', 'Password1'
  ];

  const checkRequirements = (): PasswordRequirements => {
    return {
      minLength: password.length >= (requirements.minLength || 12),
      hasUppercase: !requirements.requireUppercase || /[A-Z]/.test(password),
      hasLowercase: !requirements.requireLowercase || /[a-z]/.test(password),
      hasNumbers: !requirements.requireNumbers || /\d/.test(password),
      hasSpecialChars: !requirements.requireSpecialChars || /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>?]/.test(password),
      isNotCommon: !commonPasswords.includes(password.toLowerCase())
    };
  };

  const calculateStrength = (): { score: number; label: string; color: string } => {
    if (!password) {
      return { score: 0, label: 'Enter a password', color: 'gray' };
    }

    const reqs = checkRequirements();
    let score = 0;

    // Base requirements (60% of score)
    if (reqs.minLength) score += 15;
    if (reqs.hasUppercase) score += 10;
    if (reqs.hasLowercase) score += 10;
    if (reqs.hasNumbers) score += 10;
    if (reqs.hasSpecialChars) score += 15;

    // Additional security checks (40% of score)
    if (reqs.isNotCommon) score += 10;
    if (password.length >= 16) score += 10;
    if (password.length >= 20) score += 5;
    
    // Check for character variety
    const uniqueChars = new Set(password.toLowerCase()).size;
    if (uniqueChars >= 8) score += 5;
    if (uniqueChars >= 12) score += 5;

    // Check for patterns (reduce score)
    if (/^(.)\1+$/.test(password)) score -= 30; // All same character
    if (/^(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)+$/i.test(password)) {
      score -= 20; // Simple sequence
    }

    score = Math.max(0, Math.min(100, score));

    if (score < 30) {
      return { score, label: 'Very Weak', color: 'red' };
    } else if (score < 50) {
      return { score, label: 'Weak', color: 'red' };
    } else if (score < 70) {
      return { score, label: 'Fair', color: 'yellow' };
    } else if (score < 85) {
      return { score, label: 'Good', color: 'blue' };
    } else {
      return { score, label: 'Excellent', color: 'green' };
    }
  };

  const strength = calculateStrength();
  const reqs = checkRequirements();
  const isValid = Object.values(reqs).every(req => req);

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'red':
        return {
          bg: 'bg-red-500',
          text: 'text-red-700',
          light: 'bg-red-100'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-500',
          text: 'text-yellow-700',
          light: 'bg-yellow-100'
        };
      case 'blue':
        return {
          bg: 'bg-blue-500',
          text: 'text-blue-700',
          light: 'bg-blue-100'
        };
      case 'green':
        return {
          bg: 'bg-green-500',
          text: 'text-green-700',
          light: 'bg-green-100'
        };
      default:
        return {
          bg: 'bg-gray-300',
          text: 'text-gray-700',
          light: 'bg-gray-100'
        };
    }
  };

  const colors = getColorClasses(strength.color);

  return (
    <div className="space-y-3">
      {/* Strength Bar */}
      {password && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Password Strength</span>
            <span className={`text-sm font-medium ${colors.text}`}>
              {strength.label}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${colors.bg}`}
              style={{ width: `${strength.score}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Requirements List */}
      {showRequirements && password && (
        <div className={`rounded-lg p-4 border ${
          isValid ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center mb-3">
            <FaShieldAlt className={isValid ? 'text-green-600' : 'text-gray-400'} />
            <span className="ml-2 text-sm font-medium text-gray-900">
              Password Requirements
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <RequirementItem
              met={reqs.minLength}
              text={`At least ${requirements.minLength || 12} characters`}
            />
            {requirements.requireUppercase && (
              <RequirementItem
                met={reqs.hasUppercase}
                text="One uppercase letter"
              />
            )}
            {requirements.requireLowercase && (
              <RequirementItem
                met={reqs.hasLowercase}
                text="One lowercase letter"
              />
            )}
            {requirements.requireNumbers && (
              <RequirementItem
                met={reqs.hasNumbers}
                text="One number"
              />
            )}
            {requirements.requireSpecialChars && (
              <RequirementItem
                met={reqs.hasSpecialChars}
                text="One special character"
              />
            )}
            <RequirementItem
              met={reqs.isNotCommon}
              text="Not a common password"
            />
          </div>
        </div>
      )}

      {/* Security Tips */}
      {password && strength.score < 70 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Tips for a stronger password:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Use a mix of uppercase and lowercase letters</li>
            <li>• Include numbers and special characters</li>
            <li>• Make it at least 12 characters long</li>
            <li>• Avoid common words and patterns</li>
            <li>• Consider using a passphrase</li>
          </ul>
        </div>
      )}
    </div>
  );
};

interface RequirementItemProps {
  met: boolean;
  text: string;
}

const RequirementItem: React.FC<RequirementItemProps> = ({ met, text }) => (
  <div className={`flex items-center space-x-2 ${met ? 'text-green-700' : 'text-gray-600'}`}>
    {met ? (
      <FaCheck className="w-3 h-3 text-green-600" />
    ) : (
      <FaTimes className="w-3 h-3 text-gray-400" />
    )}
    <span className="text-sm">{text}</span>
  </div>
);